import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

const JobStatusMonitor = ({ jobId, onComplete, onError }) => {
  const { instance, accounts } = useMsal();
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const jobStatusRef = useRef(null);

  const fetchJobStatus = useCallback(async () => {
    try {
      if (!accounts || accounts.length === 0) {
        throw new Error('No authenticated user found');
      }

      const statusResponse = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/matrix/job/${jobId}`
      );

      if (statusResponse.data.success) {
        const newJobStatus = statusResponse.data.data;
        setJobStatus(newJobStatus);
        jobStatusRef.current = newJobStatus;
        
        // Check if job is complete or failed
        if (newJobStatus.status === 'completed') {
          onComplete && onComplete(newJobStatus);
        } else if (newJobStatus.status === 'failed') {
          onError && onError(newJobStatus.error_message);
        }
      } else {
        setError('Failed to fetch job status');
      }
    } catch (error) {
      console.error('Error fetching job status:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [accounts, instance, jobId, onComplete, onError]);

  useEffect(() => {
    if (jobId) {
      fetchJobStatus();
      
      // Poll for status updates every 2 seconds
      const interval = setInterval(() => {
        // Only continue polling if job is still active
        const currentStatus = jobStatusRef.current;
        if (currentStatus && (currentStatus.status === 'queued' || currentStatus.status === 'processing')) {
          fetchJobStatus();
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [jobId, fetchJobStatus]);

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '2px solid #f3f3f3',
          borderTop: '2px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 0.5rem'
        }}></div>
        <p>Loading job status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#dc3545' }}>
        <p>Error: {error}</p>
      </div>
    );
  }

  if (!jobStatus) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <p>No job status available</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'queued': return '#ffc107';
      case 'processing': return '#007bff';
      case 'completed': return '#28a745';
      case 'failed': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'queued': return '⏳';
      case 'processing': return '🔄';
      case 'completed': return '✅';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  return (
    <div style={{
      padding: '1rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      backgroundColor: '#f8f9fa',
      marginBottom: '1rem'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem'
      }}>
        <h4 style={{ margin: 0, fontSize: '1rem' }}>
          {getStatusIcon(jobStatus.status)} Job Status
        </h4>
        <span style={{
          padding: '0.25rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.8rem',
          fontWeight: 'bold',
          backgroundColor: getStatusColor(jobStatus.status),
          color: 'white'
        }}>
          {jobStatus.status.toUpperCase()}
        </span>
      </div>
      
      <div style={{ fontSize: '0.9rem', color: '#666' }}>
        <p style={{ margin: '0.25rem 0' }}>
          <strong>Job ID:</strong> {jobStatus.job_id}
        </p>
        <p style={{ margin: '0.25rem 0' }}>
          <strong>Project:</strong> {jobStatus.job_number} - {jobStatus.job_name}
        </p>
        <p style={{ margin: '0.25rem 0' }}>
          <strong>Total Rows:</strong> {jobStatus.total_rows}
        </p>
        {jobStatus.progress !== null && (
          <p style={{ margin: '0.25rem 0' }}>
            <strong>Progress:</strong> {jobStatus.progress}%
          </p>
        )}
        {jobStatus.error_message && (
          <p style={{ margin: '0.25rem 0', color: '#dc3545' }}>
            <strong>Error:</strong> {jobStatus.error_message}
          </p>
        )}
        <p style={{ margin: '0.25rem 0' }}>
          <strong>Created:</strong> {new Date(jobStatus.created_at).toLocaleString()}
        </p>
        <p style={{ margin: '0.25rem 0' }}>
          <strong>Updated:</strong> {new Date(jobStatus.updated_at).toLocaleString()}
        </p>
      </div>

      {/* Progress Bar */}
      {jobStatus.progress !== null && jobStatus.status === 'processing' && (
        <div style={{ marginTop: '1rem' }}>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: '#e9ecef',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${jobStatus.progress}%`,
              height: '100%',
              backgroundColor: '#007bff',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobStatusMonitor;
